import React, { useEffect, useRef, useState } from "react";
import { Upload, Paperclip, Trash2, FileText, Image as ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, getToken, API } from "@/lib/api";

const CATEGORIES = [
  { value: "referral_form", label: "Referral form" },
  { value: "clinical", label: "Clinical document" },
  { value: "lab", label: "Lab report" },
  { value: "radiology", label: "Radiology report" },
  { value: "visit_evidence", label: "Visit evidence (photos)" },
  { value: "other", label: "Other" },
];

function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function AttachmentsPanel({ requestId, defaultCategory = "referral_form", title = "Attachments", readOnly = false }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState(defaultCategory);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  async function load() {
    try {
      const res = await api.get("/uploads", { params: { request_id: requestId } });
      setItems(res.data.items || []);
    } catch (e) { /* silent */ }
  }
  useEffect(() => { if (requestId) load(); /* eslint-disable-next-line */ }, [requestId]);

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("request_id", requestId);
        fd.append("category", category);
        await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(att) {
    try {
      await api.delete(`/uploads/${att.id}`);
      toast.success("Attachment removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function viewUrl(att) {
    const token = getToken();
    return `${API}/uploads/${att.id}/download?auth=${encodeURIComponent(token)}`;
  }

  return (
    <div className="hah-card p-5" data-testid="ob-attachments-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="hah-section-title">Files</div>
          <h3 className="text-base font-semibold text-[#1F2937]">{title}</h3>
          <p className="text-sm text-[#6B7280]">Referral form, clinical documents, lab/radiology reports.</p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[200px] h-9 border-[#E5E7EB]" data-testid="ob-attachment-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              data-testid="ob-attachment-file"
              onChange={handleUpload}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
              data-testid="ob-attachment-upload"
            >
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-[#6B7280] py-6 text-center border border-dashed border-[#E5E7EB] rounded-lg">
          No attachments yet.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((att) => {
            const isImage = (att.content_type || "").startsWith("image/");
            const Icon = isImage ? ImageIcon : FileText;
            return (
              <div key={att.id} className="flex items-center gap-3 p-3 border border-[#E5E7EB] rounded-lg hover:bg-[#F5F7FA]/40">
                <div className="w-10 h-10 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#1F2937] text-sm truncate">{att.original_filename}</div>
                  <div className="text-xs text-[#6B7280]">
                    {CATEGORIES.find((c) => c.value === att.category)?.label || att.category || "—"} · {fmtSize(att.size)} · uploaded by {att.uploaded_by_email || "—"}
                  </div>
                </div>
                <a
                  href={viewUrl(att)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`ob-attachment-view-${att.id}`}
                  className="text-[#0B7E8C] hover:underline text-sm inline-flex items-center gap-1"
                >
                  <Download className="w-4 h-4" /> View
                </a>
                {!readOnly && (
                  <button
                    onClick={() => remove(att)}
                    data-testid={`ob-attachment-delete-${att.id}`}
                    className="text-[#EF4444] hover:bg-[#EF444412] p-1.5 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
